import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { FaCalculator, FaFileSignature, FaHandHoldingUsd, FaPercent, FaClock, FaShieldAlt } from 'react-icons/fa';
import { BiDollarCircle } from 'react-icons/bi';
import { RiShieldCheckFill } from 'react-icons/ri';

const Financing = () => {
  const fadeIn = {
    initial: { opacity: 0, y: 20 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.6 }
  };

  // Add calculator state
  const [purchaseAmount, setPurchaseAmount] = useState('');
  const [termLength, setTermLength] = useState(12);
  const [interestRate, setInterestRate] = useState(0);

  // Format currency input
  const formatCurrency = (value) => {
    const number = value.replace(/[^\d]/g, '');
    if (!number) return '';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(number);
  };

  // Handle purchase amount change
  const handlePurchaseAmountChange = (e) => {
    const value = e.target.value.replace(/[^\d]/g, '');
    setPurchaseAmount(value);
  };

  // Handle term length change
  const handleTermLengthChange = (e) => {
    const term = parseInt(e.target.value);
    setTermLength(term);
    // Update interest rate based on term
    setInterestRate(term === 12 ? 0 : 4.99);
  };

  // Calculate monthly payment
  const calculateMonthlyPayment = () => {
    if (!purchaseAmount || !termLength) return 0;
    
    const principal = parseFloat(purchaseAmount);
    const monthlyRate = (interestRate / 100) / 12;
    const numberOfPayments = termLength;

    if (interestRate === 0) {
      return principal / numberOfPayments;
    }

    const payment = principal * (monthlyRate * Math.pow(1 + monthlyRate, numberOfPayments)) / 
                   (Math.pow(1 + monthlyRate, numberOfPayments) - 1);
    
    return payment;
  };

  const financingOptions = [
    {
      title: '0% APR Financing',
      term: '12 Months',
      description: 'No interest if paid in full within 12 months',
      features: [
        'No down payment required',
        'Easy monthly payments',
        'Quick approval process',
        'No prepayment penalties'
      ],
      requirements: [
        'Minimum purchase of $1,000',
        'Credit score 640+',
        'Proof of income',
        'Valid ID'
      ]
    },
    {
      title: 'Low-Rate Financing',
      term: '60 Months',
      description: 'Fixed monthly payments with competitive interest rates',
      features: [
        'Rates as low as 4.99% APR',
        'Flexible payment terms',
        'Fixed monthly payments',
        'No hidden fees'
      ],
      requirements: [
        'Minimum purchase of $5,000',
        'Credit score 620+',
        'Proof of income',
        'Valid ID'
      ]
    },
    {
      title: 'FHA Title I Loans',
      term: 'Up to 20 Years',
      description: 'Government-backed home improvement loans',
      features: [
        'Low down payment',
        'Longer repayment terms',
        'Competitive rates',
        'No equity required'
      ],
      requirements: [
        'Owner-occupied property',
        'Credit score 580+',
        'Proof of income',
        'Property insurance'
      ]
    }
  ];

  const benefits = [
    {
      icon: <FaHandHoldingUsd className="w-10 h-10 text-primary-600" />,
      title: 'Flexible Payment Options',
      description: 'Choose from various payment plans that fit your budget'
    },
    {
      icon: <FaPercent className="w-10 h-10 text-primary-600" />,
      title: 'Competitive Rates',
      description: 'Access industry-leading interest rates and terms'
    },
    {
      icon: <FaClock className="w-10 h-10 text-primary-600" />,
      title: 'Quick Approval',
      description: 'Get approved within minutes, not days'
    },
    {
      icon: <FaShieldAlt className="w-10 h-10 text-primary-600" />,
      title: 'Secure Process',
      description: 'Your information is protected with bank-level security'
    }
  ];

  const faqs = [
    {
      question: 'What credit score do I need to qualify?',
      answer: 'We offer financing options for various credit scores, starting from 580+. Higher credit scores may qualify for better rates and terms.'
    },
    {
      question: 'How long does the approval process take?',
      answer: 'Most applications receive an instant decision. In some cases, additional verification may take 24-48 hours.'
    },
    {
      question: 'Is there a prepayment penalty?',
      answer: 'No, all our financing options come with no prepayment penalties. You can pay off your loan early without additional fees.'
    },
    {
      question: 'What documents do I need to apply?',
      answer: 'Generally, you will need a valid ID, proof of income (pay stubs or tax returns), and proof of residence.'
    }
  ];

  return (
    <div className="pt-24 pb-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header Section */}
        <motion.div
          initial={fadeIn.initial}
          animate={fadeIn.animate}
          transition={fadeIn.transition}
          className="text-center mb-16"
        >
          <span className="inline-block px-4 py-1 bg-primary-100 text-primary-600 rounded-full text-sm font-medium mb-4">FINANCING OPTIONS</span>
          <h1 className="text-4xl font-bold text-gray-900 mb-4">
            Flexible Financing Options
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Make your home improvement dreams a reality with our affordable financing solutions.
            We offer various payment plans to fit your budget and needs.
          </p>
        </motion.div>

        {/* Benefits Section */}
        <motion.div
          initial={fadeIn.initial}
          whileInView={fadeIn.animate}
          viewport={{ once: true }}
          transition={fadeIn.transition}
          className="grid grid-cols-1 md:grid-cols-4 gap-8 mb-16"
        >
          {benefits.map((benefit, index) => (
            <div key={index} className="text-center p-6 bg-white rounded-lg shadow-md hover:shadow-lg transition duration-300">
              <div className="flex justify-center mb-4">
                {benefit.icon}
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">{benefit.title}</h3>
              <p className="text-gray-600">{benefit.description}</p>
            </div>
          ))}
        </motion.div>

        {/* Financing Options Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16">
          {financingOptions.map((option, index) => (
            <motion.div
              key={option.title}
              initial={fadeIn.initial}
              whileInView={fadeIn.animate}
              viewport={{ once: true }}
              transition={{ ...fadeIn.transition, delay: index * 0.2 }}
              className="bg-white rounded-lg shadow-md overflow-hidden"
            >
              <div className="bg-primary-600 text-white p-6 text-center">
                <h3 className="text-2xl font-bold mb-2">{option.title}</h3>
                <p className="text-lg">{option.term}</p>
              </div>
              
              <div className="p-6">
                <p className="text-gray-600 mb-6 text-center">
                  {option.description}
                </p>

                <div className="mb-6">
                  <h4 className="font-semibold text-gray-900 mb-3">Features:</h4>
                  <ul className="space-y-2">
                    {option.features.map((feature, idx) => (
                      <li key={idx} className="flex items-center text-gray-600">
                        <RiShieldCheckFill className="w-5 h-5 text-green-500 mr-3" />
                        {feature}
                      </li>
                    ))}
                  </ul>
                </div>

                <div>
                  <h4 className="font-semibold text-gray-900 mb-3">Requirements:</h4>
                  <ul className="space-y-2">
                    {option.requirements.map((requirement, idx) => (
                      <li key={idx} className="flex items-center text-gray-600">
                        <span className="mr-3">•</span>
                        {requirement}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>

              <div className="p-6 bg-gray-50 border-t border-gray-100">
                <Link
                  to="/get-estimate"
                  className="block w-full bg-primary-600 text-white text-center px-6 py-3 rounded-md hover:bg-primary-700 transition duration-300"
                >
                  Apply Now
                </Link>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Calculator Preview Section */}
        <motion.div
          initial={fadeIn.initial}
          whileInView={fadeIn.animate}
          viewport={{ once: true }}
          transition={fadeIn.transition}
          className="bg-gray-50 rounded-lg p-12 mb-16"
        >
          <div className="flex flex-col md:flex-row items-center gap-12">
            <div className="md:w-1/2">
              <FaCalculator className="w-16 h-16 text-primary-600 mb-6" />
              <h2 className="text-3xl font-bold text-gray-900 mb-4">
                Payment Calculator
              </h2>
              <p className="text-gray-600 mb-6">
                Use our payment calculator to estimate your monthly payments and see how different terms affect your budget.
                {interestRate > 0 && <span className="block mt-2 text-sm">*{interestRate}% APR applied for terms over 12 months</span>}
              </p>
            </div>
            <div className="md:w-1/2 bg-white p-8 rounded-lg shadow-md">
              <div className="space-y-4">
                <div>
                  <label className="block text-gray-700 mb-2">Purchase Amount</label>
                  <input
                    type="text"
                    value={formatCurrency(purchaseAmount)}
                    onChange={handlePurchaseAmountChange}
                    placeholder="Enter amount"
                    className="w-full p-3 border rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  />
                </div>
                <div>
                  <label className="block text-gray-700 mb-2">Term Length</label>
                  <select
                    value={termLength}
                    onChange={handleTermLengthChange}
                    className="w-full p-3 border rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  >
                    <option value={12}>12 Months (0% APR)</option>
                    <option value={24}>24 Months</option>
                    <option value={36}>36 Months</option>
                    <option value={48}>48 Months</option>
                    <option value={60}>60 Months</option>
                  </select>
                </div>
                <div className="bg-gray-50 p-4 rounded-md">
                  <p className="text-gray-700">Estimated Monthly Payment</p>
                  <p className="text-2xl font-bold text-primary-600">
                    {new Intl.NumberFormat('en-US', {
                      style: 'currency',
                      currency: 'USD',
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    }).format(calculateMonthlyPayment())}
                  </p>
                  {purchaseAmount && (
                    <p className="text-sm text-gray-500 mt-2">
                      Total amount: {formatCurrency(purchaseAmount)}
                    </p>
                  )}
                </div>
              </div>
            </div>
          </div>
        </motion.div>

        {/* FAQ Section */}
        <motion.div
          initial={fadeIn.initial}
          whileInView={fadeIn.animate}
          viewport={{ once: true }}
          transition={fadeIn.transition}
          className="mb-16"
        >
          <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">
            Frequently Asked Questions
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {faqs.map((faq, index) => (
              <div key={index} className="bg-white rounded-lg shadow-md p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-3">{faq.question}</h3>
                <p className="text-gray-600">{faq.answer}</p>
              </div>
            ))}
          </div>
        </motion.div>

        {/* Call to Action */}
        <motion.div
          initial={fadeIn.initial}
          whileInView={fadeIn.animate}
          viewport={{ once: true }}
          transition={fadeIn.transition}
          className="text-center bg-primary-600 rounded-lg p-12 text-white"
        >
          <h2 className="text-3xl font-bold mb-4">
            Ready to Get Started?
          </h2>
          <p className="text-xl mb-8">
            Our financing experts are here to help you find the perfect payment plan.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <Link
              to="/get-estimate"
              className="bg-white text-primary-600 px-8 py-3 rounded-md text-lg font-semibold hover:bg-gray-100 transition duration-300"
            >
              Apply for Financing
            </Link>
            <Link
              to="/get-estimate"
              className="border-2 border-white text-white px-8 py-3 rounded-md text-lg font-semibold hover:bg-white hover:text-primary-600 transition duration-300"
            >
              Speak to an Expert
            </Link>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default Financing; 